import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  CircularProgress,
  DialogContentText,
  Autocomplete,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import GroupIcon from '@mui/icons-material/Group';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EventIcon from '@mui/icons-material/Event';
import { getAllUsers } from '../../services/authService';
import {
  getAllBonuses,
  getPeriodSummaries,
  addBonus,
  updateBonus,
  deleteBonus,
  addDeduction,
} from '../../services/livestreamService';

const fmtVnd = (n) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '-';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('vi-VN') : '-';

const PeriodBonusManagement = ({ isMobile }) => {
  const [bonuses, setBonuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Add dialog
  const [openAdd, setOpenAdd] = useState(false);
  const [addUser, setAddUser] = useState(null);
  const [addUserSummaries, setAddUserSummaries] = useState([]);
  const [addSummaryId, setAddSummaryId] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [addReason, setAddReason] = useState('');
  const [addKind, setAddKind] = useState('bonus'); // 'bonus' | 'deduction'
  const [addDeductionType, setAddDeductionType] = useState('late');
  const [adding, setAdding] = useState(false);

  // Edit dialog
  const [openEdit, setOpenEdit] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editing, setEditing] = useState(false);

  // Delete confirmation
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showNotification = (message, severity = 'success') =>
    setNotification({ open: true, message, severity });

  const fetchBonuses = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllBonuses();
      if (result.success) setBonuses(result.data || []);
      else showNotification(result.error?.message || 'Không thể tải danh sách thưởng', 'error');
    } catch (e) {
      showNotification('Lỗi tải danh sách thưởng', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const result = await getAllUsers();
      if (result.success) setUsers(result.data || []);
    } catch (e) {
      console.error('fetchUsers error', e);
    }
  }, []);

  useEffect(() => {
    fetchBonuses();
    fetchUsers();
  }, [fetchBonuses, fetchUsers]);

  // Khi chọn user trong dialog Thêm → load period summaries của user đó
  useEffect(() => {
    if (!addUser?._id) {
      setAddUserSummaries([]);
      setAddSummaryId('');
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await getPeriodSummaries('all', addUser._id);
      if (cancelled) return;
      if (result.success) {
        const list = result.data?.data || result.data || [];
        setAddUserSummaries(Array.isArray(list) ? list : []);
      } else {
        setAddUserSummaries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [addUser]);

  const stats = useMemo(() => {
    const total = bonuses.length;
    const totalAmount = bonuses.reduce((s, b) => s + (b.amount || 0), 0);
    const userIds = new Set(bonuses.map(b => b.user?._id).filter(Boolean));
    const now = new Date();
    const monthAmount = bonuses
      .filter(b => {
        const d = new Date(b.createdAt);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((s, b) => s + (b.amount || 0), 0);
    return { total, totalAmount, userCount: userIds.size, monthAmount };
  }, [bonuses]);

  const resetAddForm = () => {
    setAddUser(null);
    setAddUserSummaries([]);
    setAddSummaryId('');
    setAddAmount('');
    setAddReason('');
    setAddKind('bonus');
    setAddDeductionType('late');
  };

  const handleAdd = async () => {
    const amount = Number(addAmount);
    const isDeduction = addKind === 'deduction';
    if (!addSummaryId) return showNotification('Vui lòng chọn kỳ tổng kết', 'warning');
    if (!amount || amount <= 0) return showNotification('Số tiền phải > 0', 'warning');
    if (!addReason.trim()) return showNotification('Vui lòng nhập lý do', 'warning');
    setAdding(true);
    try {
      const result = isDeduction
        ? await addDeduction(addSummaryId, { amount, reason: addReason.trim(), type: addDeductionType })
        : await addBonus(addSummaryId, { amount, reason: addReason.trim() });
      if (result.success) {
        showNotification(isDeduction ? 'Đã thêm khấu trừ' : 'Đã thêm thưởng và gửi thông báo cho user');
        setOpenAdd(false);
        resetAddForm();
        fetchBonuses();
      } else {
        showNotification(result.error?.message || 'Không thể thêm', 'error');
      }
    } finally {
      setAdding(false);
    }
  };

  const openEditDialog = (b) => {
    setEditTarget(b);
    setEditAmount(String(b.amount || 0));
    setEditReason(b.reason || '');
    setOpenEdit(true);
  };

  const handleEdit = async () => {
    const amount = Number(editAmount);
    if (!amount || amount <= 0) return showNotification('Số tiền thưởng phải > 0', 'warning');
    if (!editReason.trim()) return showNotification('Vui lòng nhập lý do thưởng', 'warning');
    setEditing(true);
    try {
      const result = await updateBonus(editTarget.summaryId, editTarget._id, { amount, reason: editReason.trim() });
      if (result.success) {
        showNotification('Đã cập nhật khoản thưởng');
        setOpenEdit(false);
        setEditTarget(null);
        fetchBonuses();
      } else {
        showNotification(result.error?.message || 'Không thể cập nhật', 'error');
      }
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteBonus(deleteTarget.summaryId, deleteTarget._id);
      if (result.success) {
        showNotification('Đã xoá khoản thưởng');
        setOpenDelete(false);
        setDeleteTarget(null);
        fetchBonuses();
      } else {
        showNotification(result.error?.message || 'Không thể xoá', 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const pageItems = bonuses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CardGiftcardIcon color="warning" /> Thưởng cuối kỳ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Admin thêm/sửa/xoá khoản thưởng cộng vào tổng thu nhập của user theo từng kỳ.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Làm mới">
            <IconButton onClick={fetchBonuses}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)}>
            Thêm thưởng
          </Button>
        </Stack>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#fff8e1' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CardGiftcardIcon color="warning" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Tổng số bonus</Typography>
                  <Typography variant="h6" fontWeight={700}>{stats.total}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <MonetizationOnIcon color="success" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Tổng tiền</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmtVnd(stats.totalAmount)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EventIcon color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Tháng này</Typography>
                  <Typography variant="h6" fontWeight={700}>{fmtVnd(stats.monthAmount)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <GroupIcon sx={{ color: '#7b1fa2' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">User được thưởng</Typography>
                  <Typography variant="h6" fontWeight={700}>{stats.userCount}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table / Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : bonuses.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CardGiftcardIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
          <Typography color="text.secondary">Chưa có khoản thưởng nào. Bấm "Thêm thưởng" để bắt đầu.</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {pageItems.map(b => (
            <Card key={b._id} sx={{ border: '1px solid #ffe0b2' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography fontWeight={700}>{b.user?.fullName || '?'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Kỳ {fmtDate(b.periodStart)} — {fmtDate(b.periodEnd)}
                    </Typography>
                  </Box>
                  <Chip color="warning" label={fmtVnd(b.amount)} />
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>{b.reason}</Typography>
                <Typography variant="caption" color="text.secondary">Tạo lúc: {fmtDateTime(b.createdAt)}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(b)}>Sửa</Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => { setDeleteTarget(b); setOpenDelete(true); }}>Xoá</Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell>User</TableCell>
                <TableCell>Kỳ</TableCell>
                <TableCell align="right">Số tiền</TableCell>
                <TableCell>Lý do</TableCell>
                <TableCell>Ngày tạo</TableCell>
                <TableCell align="center">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map(b => (
                <TableRow key={b._id} hover>
                  <TableCell><Typography fontWeight={600}>{b.user?.fullName || '?'}</Typography></TableCell>
                  <TableCell>{fmtDate(b.periodStart)} — {fmtDate(b.periodEnd)}</TableCell>
                  <TableCell align="right">
                    <Typography color="warning.main" fontWeight={700}>{fmtVnd(b.amount)}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Tooltip title={b.reason || ''}>
                      <Typography variant="body2" noWrap>{b.reason}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell><Typography variant="caption">{fmtDateTime(b.createdAt)}</Typography></TableCell>
                  <TableCell align="center">
                    <Tooltip title="Sửa"><IconButton size="small" onClick={() => openEditDialog(b)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Xoá"><IconButton size="small" color="error" onClick={() => { setDeleteTarget(b); setOpenDelete(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={bonuses.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}

      {/* Add Dialog */}
      <Dialog open={openAdd} onClose={() => !adding && setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Thêm thưởng / khấu trừ cuối kỳ</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={users}
              value={addUser}
              onChange={(_, v) => setAddUser(v)}
              getOptionLabel={(o) => o ? `${o.fullName} (${o.username})` : ''}
              renderInput={(params) => <TextField {...params} label="Chọn user" />}
            />
            <TextField
              select
              label="Kỳ tổng kết"
              value={addSummaryId}
              onChange={(e) => setAddSummaryId(e.target.value)}
              disabled={!addUser || addUserSummaries.length === 0}
              helperText={addUser && addUserSummaries.length === 0 ? 'User này chưa có kỳ tổng kết nào' : ''}
            >
              {addUserSummaries.map(s => (
                <MenuItem key={s._id} value={s._id}>
                  {fmtDate(s.periodStart)} — {fmtDate(s.periodEnd)} • Tổng hiện tại: {fmtVnd(s.grandTotal ?? s.totalEarnings)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Loại khoản"
              value={addKind}
              onChange={(e) => setAddKind(e.target.value)}
            >
              <MenuItem value="bonus">Thưởng (cộng)</MenuItem>
              <MenuItem value="deduction">Khấu trừ / chế tài (trừ)</MenuItem>
            </TextField>
            {addKind === 'deduction' && (
              <TextField
                select
                label="Loại chế tài"
                value={addDeductionType}
                onChange={(e) => setAddDeductionType(e.target.value)}
                helperText="Các khoản auto (thiếu giờ, tỷ trọng TikTok) do hệ thống tự tính khi chốt kỳ."
              >
                <MenuItem value="late">Trễ ca (điều chỉnh thủ công)</MenuItem>
                <MenuItem value="sale_absence">Vắng ngày sale</MenuItem>
                <MenuItem value="empty_slot">Khung bắt buộc bị trống (Lead)</MenuItem>
                <MenuItem value="liver_unreported">Liver thiếu giờ không báo (Lead)</MenuItem>
                <MenuItem value="manual">Khác</MenuItem>
              </TextField>
            )}
            <TextField
              label={addKind === 'deduction' ? 'Số tiền khấu trừ (VND)' : 'Số tiền thưởng (VND)'}
              type="number"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Lý do"
              multiline
              rows={3}
              value={addReason}
              onChange={(e) => setAddReason(e.target.value)}
              placeholder={addKind === 'deduction' ? 'VD: Vắng ca sale ngày 12/08...' : 'VD: Hoàn thành KPI vượt mức, hỗ trợ team...'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAdd(false); resetAddForm(); }} disabled={adding}>Huỷ</Button>
          <Button variant="contained" color={addKind === 'deduction' ? 'error' : 'warning'} onClick={handleAdd} disabled={adding}>
            {adding ? 'Đang thêm...' : (addKind === 'deduction' ? 'Thêm khấu trừ' : 'Thêm & Thông báo user')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} onClose={() => !editing && setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sửa khoản thưởng</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editTarget && (
              <Typography variant="caption" color="text.secondary">
                {editTarget.user?.fullName} • Kỳ {fmtDate(editTarget.periodStart)} — {fmtDate(editTarget.periodEnd)}
              </Typography>
            )}
            <TextField
              label="Số tiền thưởng (VND)"
              type="number"
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Lý do thưởng"
              multiline
              rows={3}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} disabled={editing}>Huỷ</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}>
            {editing ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={openDelete} onClose={() => !deleting && setOpenDelete(false)}>
        <DialogTitle>Xác nhận xoá khoản thưởng?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Khoản thưởng <b>{fmtVnd(deleteTarget?.amount)}</b> cho <b>{deleteTarget?.user?.fullName}</b> sẽ được xoá khỏi tổng thu nhập của user.
            Thông báo đã gửi sẽ bị ẩn (không hiện lại nếu user chưa đọc).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} disabled={deleting}>Huỷ</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Đang xoá...' : 'Xoá'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} onClose={() => setNotification(p => ({ ...p, open: false }))}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PeriodBonusManagement;
